import type {
  BatchNpiLookupResult,
  FoundNpiFacility,
} from "@services/facilityNetworkLink";
import { batchLookupNpis } from "@services/facilityNetworkLink";
import {
  npiLookupService,
  type NPILookupLLMSuggestion,
  type NPIResult,
} from "@services/npiLookup";
import { getSelectedCountryCode } from "@utils/countryUtils";
import {
  bareNpiFromCcn,
  resolveFacilityRegistryDisplayIds,
  type FacilityHierarchyInput,
} from "@utils/facilityUtils";
import { isValidNpi, normalizeNpi } from "@utils/npiValidation";

export type ChildFacilitySearchHit = {
  /** Batch lookup / child-link key (may be CCN when facility stored with CCN unique id). */
  npi: string;
  name: string;
  city: string;
  state: string;
  ccn: string;
  /** Display NPI when known separately from {@link npi} lookup key. */
  registryNpi?: string;
  facilityType?: string;
};

export type SearchChildFacilitiesOptions = {
  parentFacilityId?: number | null;
  /** User's facilities — rematch CCN↔NPI so UniComply rows classify as found. */
  localFacilities?: FacilityHierarchyInput[] | null;
};

const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 8;

function trimText(value: string | null | undefined): string {
  return (value || "").trim();
}

function isDigitIdentifierQuery(query: string, digits: string): boolean {
  return (
    digits.length >= 6 &&
    digits.length <= 10 &&
    /^\d[\d\s-]*$/.test(query.trim())
  );
}

function ccnHintForIdentifier(identifier: string): string {
  return isValidNpi(identifier) ? "" : identifier;
}

function displayNpiForHit(hit: ChildFacilitySearchHit): string {
  const registry = trimText(hit.registryNpi);
  if (registry && isValidNpi(registry)) return registry;
  const lookup = trimText(hit.npi);
  if (lookup && lookup !== hit.ccn && isValidNpi(lookup)) return lookup;
  return "";
}

function withRegistryIds(
  hit: ChildFacilitySearchHit,
  ids: { npi: string; ccn: string },
): ChildFacilitySearchHit {
  const ccn = ids.ccn || hit.ccn;
  const registryNpi =
    ids.npi && ids.npi !== ccn ? ids.npi : hit.registryNpi;
  return {
    ...hit,
    ccn,
    ...(registryNpi ? { registryNpi } : {}),
  };
}

function facilityLookupKey(f: FacilityHierarchyInput): string {
  const uid = bareNpiFromCcn(f.facility_unique_id);
  if (uid) return uid;
  const raw = String(f.facility_unique_id || f.ccn_number || "").trim();
  const match = raw.match(/(?:^|_)(\d{6,10})$/);
  if (match && !isValidNpi(match[1])) return match[1];
  const ids = resolveFacilityRegistryDisplayIds(f);
  return ids.npi || ids.ccn || "";
}

function facilityMatchesIdentifier(
  f: FacilityHierarchyInput,
  identifier: string,
): boolean {
  const id = normalizeNpi(identifier);
  if (!id) return false;
  const ids = resolveFacilityRegistryDisplayIds(f);
  if (ids.npi === id || ids.ccn === id) return true;
  return facilityLookupKey(f) === id;
}

/** Prefer UniComply facility_unique_id when search used CCN (or vice versa). */
export function rematchHitToLocalFacility(
  hit: ChildFacilitySearchHit,
  facilities: FacilityHierarchyInput[] | null | undefined,
): ChildFacilitySearchHit {
  if (!facilities?.length) return hit;
  const candidates = [hit.npi, hit.ccn].filter(Boolean);
  const local = facilities.find((f) =>
    candidates.some((c) => facilityMatchesIdentifier(f, c)),
  );
  if (!local) return hit;
  const ids = resolveFacilityRegistryDisplayIds(local);
  const lookupId = facilityLookupKey(local) || ids.npi || ids.ccn || hit.npi;
  return withRegistryIds(
    {
      ...hit,
      npi: lookupId,
      name: trimText(local.facility_name) || hit.name,
      city: trimText(local.city) || hit.city,
      state: trimText(local.state) || hit.state,
    },
    ids,
  );
}

export function rematchHitsToLocalFacilities(
  hits: ChildFacilitySearchHit[],
  facilities: FacilityHierarchyInput[] | null | undefined,
): ChildFacilitySearchHit[] {
  return hits.map((hit) => rematchHitToLocalFacility(hit, facilities));
}

function hitFromLocalFacility(
  f: FacilityHierarchyInput,
  searchedDigits: string,
): ChildFacilitySearchHit {
  const ids = resolveFacilityRegistryDisplayIds(f);
  const lookupId = facilityLookupKey(f) || ids.npi || ids.ccn || searchedDigits;
  return withRegistryIds(
    {
      npi: lookupId,
      name: trimText(f.facility_name) || lookupId,
      city: trimText(f.city),
      state: trimText(f.state),
      ccn: ids.ccn || ccnHintForIdentifier(searchedDigits),
    },
    ids,
  );
}

function provisionalCcnHit(ccn: string): ChildFacilitySearchHit {
  return {
    npi: ccn,
    name: `CCN ${ccn}`,
    city: "",
    state: "",
    ccn,
  };
}

function hitsFromBatchLookup(result: BatchNpiLookupResult): ChildFacilitySearchHit[] {
  const hits: ChildFacilitySearchHit[] = [];
  for (const item of result.found || []) {
    hits.push({
      npi: item.npi,
      name: trimText(item.facility_name) || item.npi,
      city: "",
      state: "",
      ccn: ccnHintForIdentifier(item.npi),
    });
  }
  for (const identifier of result.not_found || []) {
    const registryName = trimText(result.facility_name_by_npi?.[identifier]);
    hits.push({
      npi: identifier,
      name: registryName || `CCN ${identifier}`,
      city: "",
      state: "",
      ccn: ccnHintForIdentifier(identifier),
    });
  }
  return uniqueHits(hits);
}

export function formatSearchLocation(hit: ChildFacilitySearchHit): string {
  return [hit.city, hit.state].filter(Boolean).join(", ");
}

export function formatSearchSubtitle(hit: ChildFacilitySearchHit): string {
  const parts: string[] = [];
  const location = formatSearchLocation(hit);
  if (location) parts.push(location);
  const displayNpi = displayNpiForHit(hit);
  if (hit.ccn) {
    parts.push(`CCN ${hit.ccn}`);
    if (displayNpi) parts.push(`NPI ${displayNpi}`);
  } else if (displayNpi) {
    parts.push(`NPI ${displayNpi}`);
  }
  return parts.join(" · ");
}

async function enrichCcnHitRegistryNpi(
  hit: ChildFacilitySearchHit,
  countryCode: string,
): Promise<ChildFacilitySearchHit> {
  if (!hit.ccn || displayNpiForHit(hit)) return hit;
  const name = trimText(hit.name);
  if (!name || name.startsWith("CCN ")) return hit;
  try {
    const response = await npiLookupService.lookupOrganization({
      organization_name: name,
      city: hit.city || undefined,
      state: hit.state || undefined,
      country_code: countryCode,
    });
    const rows = [
      ...(response.data?.results || []),
      ...(response.data?.llm_suggestions || []),
    ];
    for (const row of rows) {
      const mapped =
        mapNpiResultToSearchHit(row as NPIResult) ||
        mapLlmSuggestionToSearchHit(row as NPILookupLLMSuggestion);
      if (!mapped) continue;
      const rowCcn = trimText(mapped.ccn);
      const rowNpi = displayNpiForHit(mapped);
      if (!rowNpi) continue;
      if (rowCcn === hit.ccn || (!rowCcn && rows.length === 1)) {
        return {
          ...hit,
          registryNpi: rowNpi,
          city: hit.city || mapped.city,
          state: hit.state || mapped.state,
        };
      }
    }
  } catch {
    /* keep CCN-only hit */
  }
  return hit;
}

async function enrichCcnHitsRegistryNpi(
  hits: ChildFacilitySearchHit[],
  countryCode: string,
): Promise<ChildFacilitySearchHit[]> {
  return Promise.all(
    hits.map((hit) => enrichCcnHitRegistryNpi(hit, countryCode)),
  );
}

export function formatReviewChipLabel(hit: ChildFacilitySearchHit): string {
  const id = hit.ccn || hit.npi;
  return id ? `${hit.name} · ${id}` : hit.name;
}

export function mapNpiResultToSearchHit(result: NPIResult): ChildFacilitySearchHit | null {
  const npi = trimText(result.npi_number);
  if (!npi) return null;
  const address = result.primary_address || result.addresses?.[0];
  const facilityType =
    trimText(result.facility_type?.display_name) || trimText(result.facility_type?.code);
  return {
    npi,
    name:
      trimText(result.site_name) ||
      trimText(result.organization_name) ||
      trimText(result.basic?.organization_name) ||
      npi,
    city: trimText(address?.city),
    state: trimText(address?.state),
    ccn: trimText(result.ccn),
    ...(facilityType ? { facilityType } : {}),
  };
}

export function mapLlmSuggestionToSearchHit(
  suggestion: NPILookupLLMSuggestion,
): ChildFacilitySearchHit | null {
  const npi = trimText(suggestion.npi_number);
  if (!npi) return null;
  const facilityType = trimText(suggestion.facility_type);
  return {
    npi,
    name: trimText(suggestion.site_name) || trimText(suggestion.organization_name) || npi,
    city: trimText(suggestion.city) || trimText(suggestion.primary_address?.city),
    state: trimText(suggestion.state) || trimText(suggestion.primary_address?.state),
    ccn: trimText(suggestion.ccn) || trimText(suggestion.ccn_number),
    ...(facilityType ? { facilityType } : {}),
  };
}

export function mapFoundNpiFacility(item: FoundNpiFacility): ChildFacilitySearchHit {
  return {
    npi: item.npi,
    name: trimText(item.facility_name) || item.npi,
    city: "",
    state: "",
    ccn: "",
  };
}

export function mapNotFoundNpi(npi: string): ChildFacilitySearchHit {
  return {
    npi,
    name: npi,
    city: "",
    state: "",
    ccn: "",
  };
}

export function mergeSelectedChildFacilities(
  current: ChildFacilitySearchHit[],
  incoming: ChildFacilitySearchHit[],
): ChildFacilitySearchHit[] {
  const seen = new Set(current.map((hit) => hit.npi));
  const next = [...current];
  incoming.forEach((hit) => {
    if (!hit.npi || seen.has(hit.npi)) return;
    seen.add(hit.npi);
    next.push(hit);
  });
  return next;
}

function uniqueHits(hits: ChildFacilitySearchHit[]): ChildFacilitySearchHit[] {
  return mergeSelectedChildFacilities([], hits).slice(0, MAX_RESULTS);
}

export async function searchChildFacilities(
  query: string,
  countryCode: string = getSelectedCountryCode(),
  options: SearchChildFacilitiesOptions = {},
): Promise<ChildFacilitySearchHit[]> {
  const trimmed = query.trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return [];

  const digits = normalizeNpi(trimmed);
  const digitQuery = isDigitIdentifierQuery(trimmed, digits);
  const localFacilities = options.localFacilities || [];

  try {
    // UniComply portfolio match first (CCN or NPI → DB unique id).
    if (digitQuery && localFacilities.length > 0) {
      const local = localFacilities.find((f) =>
        facilityMatchesIdentifier(f, digits),
      );
      if (local) {
        return enrichCcnHitsRegistryNpi(
          [hitFromLocalFacility(local, digits)],
          countryCode,
        );
      }
    }

    // Valid 10-digit NPI → NPPES number lookup only (never treat CCN as NPI).
    if (digitQuery && isValidNpi(digits)) {
      const response = await npiLookupService.lookupNPI({
        npi_number: digits,
        country_code: countryCode,
      });
      const hits = rematchHitsToLocalFacilities(
        (response.data?.results || [])
          .map(mapNpiResultToSearchHit)
          .filter((hit): hit is ChildFacilitySearchHit => hit != null),
        localFacilities,
      );
      if (hits.length > 0) {
        return uniqueHits(hits);
      }
    }

    // CCN / other 6–10 digit identifiers → child-link batch lookup (CMS + UniComply).
    if (digitQuery) {
      const parentFacilityId = options.parentFacilityId;
      if (parentFacilityId != null) {
        try {
          const batch = await batchLookupNpis(parentFacilityId, [digits]);
          const hits = await enrichCcnHitsRegistryNpi(
            rematchHitsToLocalFacilities(
              hitsFromBatchLookup(batch),
              localFacilities,
            ),
            countryCode,
          );
          if (hits.length > 0) return hits;
        } catch {
          // Fall through to provisional CCN chip so typeahead is not empty.
        }
      }
      return [provisionalCcnHit(digits)];
    }

    const response = await npiLookupService.lookupOrganization({
      organization_name: trimmed,
      country_code: countryCode,
    });
    const fromResults = (response.data?.results || [])
      .map(mapNpiResultToSearchHit)
      .filter((hit): hit is ChildFacilitySearchHit => hit != null);
    const fromSuggestions = (response.data?.llm_suggestions || [])
      .map(mapLlmSuggestionToSearchHit)
      .filter((hit): hit is ChildFacilitySearchHit => hit != null);
    return uniqueHits(
      rematchHitsToLocalFacilities(
        [...fromResults, ...fromSuggestions],
        localFacilities,
      ),
    );
  } catch {
    return digitQuery ? [provisionalCcnHit(digits)] : [];
  }
}
